import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

/**
 * Stockfish WASM analysis panel.
 * Loads engine from CDN and analyzes a given FEN.
 */
export default function StockfishPanel({ fen, depth=15 }) {
  const engineRef = useRef(null);
  const [info, setInfo] = useState({ evaluation: null, bestMove: null, depth: 0, pv: '' });
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let engine;
    try {
      // Use Stockfish from CDN via Web Worker
      const workerCode = `
        importScripts('https://cdn.jsdelivr.net/npm/stockfish.js@10.0.2/stockfish.js');
      `;
      const blob = new Blob([workerCode], { type: 'application/javascript' });
      engine = new Worker(URL.createObjectURL(blob));
      engineRef.current = engine;
      engine.onmessage = (e) => {
        const line = typeof e.data === 'string' ? e.data : (e.data?.data || '');
        if (line === 'uciok') {
          engine.postMessage('isready');
        } else if (line === 'readyok') {
          setReady(true);
        } else if (line.startsWith('info')) {
          const depthM = line.match(/depth (\d+)/);
          const cpM = line.match(/score cp (-?\d+)/);
          const mateM = line.match(/score mate (-?\d+)/);
          const pvM = line.match(/ pv (.+)$/);
          setInfo(prev => ({
            ...prev,
            depth: depthM ? parseInt(depthM[1]) : prev.depth,
            evaluation: mateM ? `M${mateM[1]}` : (cpM ? (parseInt(cpM[1])/100).toFixed(2) : prev.evaluation),
            pv: pvM ? pvM[1].split(' ').slice(0,6).join(' ') : prev.pv,
          }));
        } else if (line.startsWith('bestmove')) {
          const parts = line.split(' ');
          setInfo(prev => ({ ...prev, bestMove: parts[1] }));
          setLoading(false);
        }
      };
      engine.onerror = (e) => { setError('No se pudo cargar Stockfish'); setLoading(false); };
      engine.postMessage('uci');
    } catch (e) {
      setError(String(e));
    }
    return () => { engine && engine.terminate(); };
  }, []);

  const analyze = () => {
    if (!engineRef.current || !ready || !fen) return;
    setLoading(true);
    setInfo({ evaluation: null, bestMove: null, depth: 0, pv: '' });
    engineRef.current.postMessage('stop');
    engineRef.current.postMessage(`position fen ${fen}`);
    engineRef.current.postMessage(`go depth ${depth}`);
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="font-display font-bold">Motor Stockfish</div>
        <Button data-testid="analyze-btn" onClick={analyze} disabled={!ready || loading} size="sm" className="rounded-full bg-slate-900 hover:bg-slate-800">
          {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : 'Analizar'}
        </Button>
      </div>
      {!ready && !error && <div className="text-xs text-slate-500">Cargando motor…</div>}
      {error && <div className="text-xs text-red-600">{error}</div>}
      {ready && (
        <div className="grid grid-cols-2 gap-3 font-mono text-sm">
          <div className="bg-slate-50 rounded-lg p-2">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Eval</div>
            <div className="text-2xl font-bold text-slate-900" data-testid="stockfish-eval">{info.evaluation ?? '—'}</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-2">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Profundidad</div>
            <div className="text-2xl font-bold text-slate-900">{info.depth || '—'}</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-2 col-span-2">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Mejor jugada</div>
            <div className="text-lg font-bold text-emerald-600" data-testid="stockfish-best">{info.bestMove ?? '—'}</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-2 col-span-2 min-h-[52px]">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Línea principal</div>
            <div className="text-sm text-slate-700 break-all">{info.pv || '—'}</div>
          </div>
        </div>
      )}
    </div>
  );
}
